import React, { useRef, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Box, Button, Chip, Typography, CircularProgress } from '@mui/material';
import { Forum as ForumIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ChatWithAnnotation, MediaCategory } from '@/types/api';
import { useChatLabel } from '@/hooks/useChatLabel';
import { useChatTheme } from '../ChatThemeContext';
import { useTelegramMessages } from '../hooks/useTelegramMessages';
import MessagePanelHeader from './MessagePanelHeader';
import MessageSearchPanel from './MessageSearchPanel';
import TelegramMessageBubble from './TelegramMessageBubble';
import ScrollToBottomFab from './ScrollToBottomFab';

interface MessagePanelProps {
  chatId: number | null;
  chat: ChatWithAnnotation | null;
}

/** How long a jumped-to message keeps its highlight before it fades out. */
const HIGHLIGHT_MS = 4000;

const MessagePanel: React.FC<MessagePanelProps> = ({ chatId, chat }) => {
  const { t } = useTranslation('pages');
  const { colors } = useChatTheme();
  const chatLabel = useChatLabel();

  const [mediaType, setMediaType] = useState<MediaCategory | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // Set by a search jump: the window is rebuilt around this message instead of
  // around the newest one. null = live view at the bottom of the channel.
  const [anchorId, setAnchorId] = useState<number | null>(null);
  // Bumped on every jump, so re-jumping to the id you are already anchored on
  // still rebuilds the window instead of doing nothing.
  const [anchorNonce, setAnchorNonce] = useState(0);
  const [highlightId, setHighlightId] = useState<number | null>(null);

  const {
    messages,
    isLoadingInitial,
    isLoadingOlder,
    isLoadingNewer,
    hasMoreOlder,
    hasMoreNewer,
    loadFailed,
    reload,
    loadOlderMessages,
    loadNewerMessages,
  } = useTelegramMessages(chatId, { mediaType, anchorId, anchorNonce });

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef(0);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const initialScrollDone = useRef(false);

  // Scroll to bottom on initial load. In an anchored view the anchor is the
  // last row of the first page, so "bottom" lands exactly on it.
  useEffect(() => {
    if (!isLoadingInitial && messages.length > 0 && !initialScrollDone.current) {
      initialScrollDone.current = true;
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [isLoadingInitial, messages.length]);

  // Every reload of the window — new chat, new filter, new anchor — re-arms it.
  // Closing the search panel counts too: the scroller is unmounted while search
  // is open, so it comes back at scrollTop 0 and has to be sent down again.
  useEffect(() => {
    initialScrollDone.current = false;
  }, [chatId, mediaType, anchorId, anchorNonce, searchOpen]);

  // A new channel drops the search and the jump; the media filter is a
  // deliberate, visible choice and survives.
  useEffect(() => {
    setSearchOpen(false);
    setAnchorId(null);
    setHighlightId(null);
  }, [chatId]);

  useEffect(() => {
    if (highlightId === null) return;
    const timer = window.setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
    // The nonce is a dep so a repeat jump to the same id re-arms the highlight.
  }, [highlightId, anchorNonce]);

  // Preserve scroll position when older messages are prepended
  useLayoutEffect(() => {
    if (!isLoadingOlder && scrollRef.current && prevScrollHeightRef.current > 0) {
      const newScrollHeight = scrollRef.current.scrollHeight;
      const diff = newScrollHeight - prevScrollHeightRef.current;
      if (diff > 0) {
        scrollRef.current.scrollTop += diff;
      }
      prevScrollHeightRef.current = 0;
    }
  }, [messages.length, isLoadingOlder]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Load older when scrolled near top
    if (el.scrollTop < 200 && hasMoreOlder && !isLoadingOlder) {
      prevScrollHeightRef.current = el.scrollHeight;
      loadOlderMessages();
    }

    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;

    // Only an anchored view has anything newer to fetch.
    if (distFromBottom < 200 && hasMoreNewer && !isLoadingNewer) {
      loadNewerMessages();
    }

    // Show/hide scroll-to-bottom
    setShowScrollBtn(distFromBottom > 300);
  }, [
    hasMoreOlder,
    isLoadingOlder,
    loadOlderMessages,
    hasMoreNewer,
    isLoadingNewer,
    loadNewerMessages,
  ]);

  const returnToLatest = useCallback(() => {
    setAnchorId(null);
    setHighlightId(null);
  }, []);

  const scrollToBottom = useCallback(() => {
    // In an anchored view "bottom" is the bottom of a window in the middle of
    // the channel, which is not what the button promises — go live instead.
    if (anchorId !== null) {
      returnToLatest();
      return;
    }
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [anchorId, returnToLatest]);

  const handleJump = useCallback((messageId: number) => {
    setAnchorId(messageId);
    // Re-jumping to the current anchor changes no state on its own — after
    // paging away from it, nothing would move the view back without this.
    setAnchorNonce((n) => n + 1);
    setHighlightId(messageId);
    setSearchOpen(false);
  }, []);

  // No chat selected
  if (!chatId || !chat) {
    return (
      <Box
        sx={{
          flex: 1,
          bgcolor: colors.chatBg,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <ForumIcon sx={{ fontSize: 64, color: colors.bubbleTimestamp }} />
        <Typography variant="h6" sx={{ color: colors.bubbleTimestamp }}>
          {t('chatPreview.messagePanel.selectChannel')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      <MessagePanelHeader
        chat={chat}
        mediaType={mediaType}
        onMediaTypeChange={setMediaType}
        searchOpen={searchOpen}
        onToggleSearch={() => setSearchOpen((open) => !open)}
      />

      {searchOpen ? (
        <MessageSearchPanel
          chatId={chatId}
          chatName={chatLabel(chat)}
          mediaType={mediaType}
          onJump={handleJump}
          onClose={() => setSearchOpen(false)}
        />
      ) : (
        <>
          {(mediaType || anchorId !== null) && (
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                bgcolor: colors.sidebarHeaderBg,
                borderBottom: `1px solid ${colors.searchBorder}`,
              }}
            >
              {mediaType && (
                <Chip
                  size="small"
                  onDelete={() => setMediaType(null)}
                  label={t('chatPreview.mediaFilter.active', {
                    // Isolated: a Latin category (PDF) at the end of an Arabic
                    // label drags the neutral colon to the wrong side.
                    category: `⁨${t(`mediaCategories.${mediaType}`)}⁩`,
                  })}
                />
              )}
              {anchorId !== null && (
                <>
                  <Typography variant="caption" sx={{ color: colors.searchText }}>
                    {/* The id is isolated so it cannot flip the Arabic around it. */}
                    {t('chatPreview.search.jumpedTo', { id: `⁨#${anchorId}⁩` })}
                  </Typography>
                  <Button size="small" onClick={returnToLatest}>
                    {t('chatPreview.search.returnToLatest')}
                  </Button>
                </>
              )}
            </Box>
          )}

          <Box
            ref={scrollRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              bgcolor: colors.chatBg,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: 1,
              py: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Loading older indicator */}
            {isLoadingOlder && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} sx={{ color: colors.bubbleTimestamp }} />
              </Box>
            )}

            {/* Initial loading */}
            {isLoadingInitial ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1 }}>
                <CircularProgress size={32} sx={{ color: colors.bubbleTimestamp }} />
                <Typography variant="body2" sx={{ color: colors.bubbleTimestamp }}>
                  {t('chatPreview.messagePanel.loading')}
                </Typography>
              </Box>
            ) : loadFailed ? (
              /* A failed first page used to render as "no messages in this
                 channel" — indistinguishable from an empty one, and with no
                 way back short of reloading the page. */
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 1 }}>
                <Typography variant="body2" sx={{ color: colors.bubbleTimestamp }}>
                  {t('chatPreview.messagePanel.loadFailed')}
                </Typography>
                <Button size="small" onClick={reload}>
                  {t('chatPreview.messagePanel.retry')}
                </Button>
              </Box>
            ) : messages.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                <Typography variant="body2" sx={{ color: colors.bubbleTimestamp }}>
                  {mediaType
                    ? t('chatPreview.mediaFilter.noMatches')
                    : t('chatPreview.messagePanel.noMessages')}
                </Typography>
              </Box>
            ) : (
              messages.map((msg) => (
                <TelegramMessageBubble
                  key={`${msg.chats_id}-${msg.message_id}`}
                  message={msg}
                  peerId={chat.chat_id}
                  highlighted={msg.message_id === highlightId}
                />
              ))
            )}

            {isLoadingNewer && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} sx={{ color: colors.bubbleTimestamp }} />
              </Box>
            )}
          </Box>

          <ScrollToBottomFab
            visible={showScrollBtn || anchorId !== null}
            onClick={scrollToBottom}
          />
        </>
      )}
    </Box>
  );
};

export default MessagePanel;
